import React, { useState, useEffect, useRef } from 'react';
import CountUp from 'react-countup';
import { FaEye, FaCheck, FaTimes, FaTrash, FaSearch, FaDownload, FaBuilding, FaUserTag, FaChevronUp, FaChevronDown, FaUndo } from 'react-icons/fa';
import { FiCalendar, FiCheckCircle, FiClock, FiDownload, FiFilter, FiList, FiTrash2, FiXCircle, FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { isEmployeeHidden } from '../utils/employeeStatus';

const API_BASE_URL = 'https://api.timelyhealth.in';

export default function ClaimedOTManagement() {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState({
    totalClaims: 0,
    totalOTHours: 0,
    pendingCount: 0,
    approvedCount: 0,
    rejectedCount: 0,
    totalPages: 1,
    currentPage: 1,
    perPage: 10
  });
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const [filters, setFilters] = useState({
    status: 'all',
    employeeId: '',
    fromDate: '',
    toDate: '',
    search: ''
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = localStorage.getItem('claimedOT_itemsPerPage');
    return saved ? parseInt(saved, 10) : 10;
  });

  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [showBulkActionModal, setShowBulkActionModal] = useState(false);
  const [selectedClaims, setSelectedClaims] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const [statusUpdate, setStatusUpdate] = useState({
    status: 'approved',
    rejectedReason: '',
    notes: ''
  });

  const [multipliers, setMultipliers] = useState({});
  const [saveStatus, setSaveStatus] = useState('');
  const saveStatusTimeoutRef = React.useRef(null);

  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterDesignation, setFilterDesignation] = useState('');
  const [showDepartmentFilter, setShowDepartmentFilter] = useState(false);
  const [showDesignationFilter, setShowDesignationFilter] = useState(false);

  const [uniqueDepartments, setUniqueDepartments] = useState([]);
  const [uniqueDesignations, setUniqueDesignations] = useState([]);

  const departmentFilterRef = useRef(null);
  const designationFilterRef = useRef(null);

  const showSaveStatus = (msg) => {
    setSaveStatus(msg);
    clearTimeout(saveStatusTimeoutRef.current);
    saveStatusTimeoutRef.current = setTimeout(() => setSaveStatus(''), 3500);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (departmentFilterRef.current && !departmentFilterRef.current.contains(event.target)) {
        setShowDepartmentFilter(false);
      }
      if (designationFilterRef.current && !designationFilterRef.current.contains(event.target)) {
        setShowDesignationFilter(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchClaims();
  }, [currentPage, filters, itemsPerPage]);

  const fetchClaims = async () => {
    try {
      setLoading(true);
      let url = `${API_BASE_URL}/api/employees/allotclaimed?page=${currentPage}&limit=${itemsPerPage}`;

      if (filters.status !== 'all') url += `&status=${filters.status}`;
      if (filters.employeeId) url += `&employeeId=${filters.employeeId}`;
      if (filters.fromDate) url += `&fromDate=${filters.fromDate}`;
      if (filters.toDate) url += `&toDate=${filters.toDate}`;

      const response = await fetch(url);
      const data = await response.json();

      if (data.success) {
        const filteredClaims = (data.claims || []).filter(claim => {
          const emp = claim.employeeDetails;
          if (emp) {
            return !isEmployeeHidden(emp);
          }
          return true;
        });

        setClaims(filteredClaims);
        setSummary({
          ...data.summary,
          totalClaims: filteredClaims.length,
          pendingCount: filteredClaims.filter(c => c.status === 'pending').length,
          approvedCount: filteredClaims.filter(c => c.status === 'approved').length,
          rejectedCount: filteredClaims.filter(c => c.status === 'rejected').length,
          totalPages: Math.ceil(filteredClaims.length / itemsPerPage) || 1,
          currentPage: currentPage,
          perPage: itemsPerPage
        });

        const depts = new Set();
        const designations = new Set();
        filteredClaims.forEach(claim => {
          if (claim.employeeDetails?.department) depts.add(claim.employeeDetails.department);
          if (claim.employeeDetails?.designation || claim.employeeDetails?.role) {
            designations.add(claim.employeeDetails.designation || claim.employeeDetails.role);
          }
        });
        setUniqueDepartments(Array.from(depts).sort());
        setUniqueDesignations(Array.from(designations).sort());

        const initialMultipliers = {};
        filteredClaims.forEach(claim => {
          const key = `otMultiplier_${claim._id}`;
          const saved = localStorage.getItem(key);
          initialMultipliers[claim._id] = saved ? parseFloat(saved) : (claim.multiplier || 2);
        });
        setMultipliers(initialMultipliers);
      } else {
        setError(data.message || 'Failed to fetch claims');
      }
    } catch (err) {
      console.error('Error fetching claims:', err);
      setError('Error fetching claims');
      toast.error('Failed to load OT claims');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      employeeId: '',
      fromDate: '',
      toDate: '',
      search: ''
    });
    setFilterDepartment('');
    setFilterDesignation('');
    setCurrentPage(1);
    if (window.innerWidth < 640) {
      setShowMobileFilters(false);
    }
  };

  const calculateOTAmountNumber = (claim, multiplierOverride) => {
    const salaryPerMonth = claim.employeeDetails?.salaryPerMonth || 0;
    const otHours = claim.otHours || 0;
    const multiplier = multiplierOverride !== undefined ? multiplierOverride : (multipliers[claim._id] || 2);

    if (!salaryPerMonth || !otHours) return 0;

    const dailySalary = salaryPerMonth / 26;
    const hourlyRate = dailySalary / 8;
    const otAmount = hourlyRate * multiplier * otHours;

    return Math.round(otAmount * 100) / 100;
  };

  const calculateOTAmount = (claim) => {
    const amount = calculateOTAmountNumber(claim);
    return `₹${amount.toFixed(2)}`;
  };

  const getMultiplier = (claimId) => {
    return multipliers[claimId] || 2;
  };

  const handleMultiplierChange = (claimId, value) => {
    const multiplier = parseFloat(value);
    setMultipliers(prev => ({ ...prev, [claimId]: multiplier }));
    localStorage.setItem(`otMultiplier_${claimId}`, multiplier);
  };

  const exportCSV = () => {
    if (claims.length === 0) {
      toast.warning('No data to export');
      return;
    }

    const headers = [
      'Employee ID', 'Employee Name', 'Department', 'Date',
      'OT Hours', 'Multiplier', 'OT Amount', 'Reason',
      'Status', 'Approved By', 'Approved At'
    ];

    const rows = claims.map(claim => [
      claim.employeeId || '',
      claim.employeeName || '',
      claim.employeeDetails?.department || '',
      formatDate(claim.date) || '',
      claim.otHours || 0,
      `${getMultiplier(claim._id)}x`,
      calculateOTAmount(claim),
      claim.reason || '',
      claim.status || '',
      claim.approvedBy || '',
      claim.approvedAt ? formatDate(claim.approvedAt) : ''
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `OT_Claims_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showSaveStatus('✅ CSV exported successfully!');
  };

  // ============================================
  // ✅ STATUS UPDATE (works for pending, approved, rejected)
  // ============================================
  const handleStatusUpdate = async () => {
    try {
      const otAmount = calculateOTAmountNumber(selectedClaim);
      const multiplier = getMultiplier(selectedClaim._id);

      const payload = {
        status: statusUpdate.status,
        rejectedReason: statusUpdate.status === 'rejected' ? statusUpdate.rejectedReason : undefined,
        notes: statusUpdate.notes || undefined,
        otAmount: otAmount,
        multiplier: multiplier
      };

      const response = await fetch(`${API_BASE_URL}/api/employees/update-otclaimedstatus/${selectedClaim._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        const actionWord = selectedClaim.status === 'approved' && statusUpdate.status === 'rejected' ? 'reverted' : statusUpdate.status;
        showSaveStatus(`✅ Claim ${actionWord} successfully! OT Amount: ₹${otAmount.toFixed(2)} at ${multiplier}x`);
        setShowStatusModal(false);
        setSelectedClaim(null);
        fetchClaims();
      } else {
        toast.error(data.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error updating status');
    }
  };

  // ============================================
  // ✅ REVERT HANDLER — Quick revert approved claim
  // ============================================
  const handleRevertApproved = async () => {
    if (!selectedClaim) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/employees/update-otclaimedstatus/${selectedClaim._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'pending',
          otAmount: 0,
          multiplier: getMultiplier(selectedClaim._id),
          notes: 'Reverted to pending by admin'
        })
      });

      const data = await response.json();
      if (data.success) {
        showSaveStatus(`✅ Approved OT reverted to pending for ${selectedClaim.employeeName}`);
        setShowRevertModal(false);
        setSelectedClaim(null);
        fetchClaims();
      } else {
        toast.error(data.message || 'Failed to revert');
      }
    } catch (error) {
      console.error('Error reverting:', error);
      toast.error('Error reverting approved OT');
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/employees/delete-claim/${selectedClaim._id}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        showSaveStatus('✅ Claim deleted successfully!');
        setShowDeleteModal(false);
        setSelectedClaim(null);
        fetchClaims();
      } else {
        toast.error(data.message || 'Failed to delete claim');
      }
    } catch (error) {
      console.error('Error deleting claim:', error);
      toast.error('Error deleting claim');
    }
  };

  const handleBulkAction = async (action, status) => {
    if (selectedClaims.length === 0) {
      toast.warning('Please select at least one claim');
      return;
    }

    try {
      let url = '';
      let body = {};

      if (action === 'status') {
        const selectedClaimsData = claims.filter(c => selectedClaims.includes(c._id));
        const totalOTAmount = selectedClaimsData.reduce((sum, claim) => {
          return sum + calculateOTAmountNumber(claim);
        }, 0);
        const multipliersData = {};
        selectedClaimsData.forEach(claim => {
          multipliersData[claim._id] = getMultiplier(claim._id);
        });

        url = `${API_BASE_URL}/api/employees/bulk-update-status`;
        body = {
          claimIds: selectedClaims,
          status: status,
          ...(status === 'rejected' && { rejectedReason: 'Bulk rejection' }),
          otAmount: Math.round(totalOTAmount * 100) / 100,
          multipliers: multipliersData
        };
      } else if (action === 'delete') {
        url = `${API_BASE_URL}/api/employees/bulk-delete`;
        body = { claimIds: selectedClaims };
      }

      const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (data.success) {
        showSaveStatus(`✅ ${data.message}`);
        setSelectedClaims([]);
        setSelectAll(false);
        setShowBulkActionModal(false);
        fetchClaims();
      } else {
        toast.error(data.message || 'Bulk action failed');
      }
    } catch (error) {
      console.error('Error in bulk action:', error);
      toast.error('Error performing bulk action');
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedClaims([]);
    } else {
      setSelectedClaims(claims.map(c => c._id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectClaim = (claimId) => {
    if (selectedClaims.includes(claimId)) {
      setSelectedClaims(selectedClaims.filter(id => id !== claimId));
    } else {
      setSelectedClaims([...selectedClaims, claimId]);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      approved: 'bg-green-100 text-green-800 border-green-300',
      rejected: 'bg-red-100 text-red-800 border-red-300'
    };
    const labels = {
      pending: '🟡 Pending',
      approved: '🟢 Approved',
      rejected: '🔴 Rejected'
    };
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${styles[status] || 'bg-gray-100'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  const getPageNumbers = () => {
    const pages = [];
    const delta = 2;
    const total = summary.totalPages || 1;
    for (let i = Math.max(1, currentPage - delta); i <= Math.min(total, currentPage + delta); i++) {
      pages.push(i);
    }
    return pages;
  };

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
    localStorage.setItem('claimedOT_itemsPerPage', String(limit));
  };

  const hasActiveFilters = filters.status !== 'all' || filters.employeeId || filters.fromDate || filters.toDate || filterDepartment || filterDesignation;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-b-2 border-blue-600 rounded-full animate-spin"></div>
          <p className="text-lg font-semibold text-gray-700">Loading OT Claims...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="p-4 text-red-600 bg-red-100 rounded-lg">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4 sm:p-6 lg:p-8">
        {saveStatus && (
          <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg font-semibold text-white animate-fade-in ${
            saveStatus.includes('✅') ? 'bg-green-600 border-l-4 border-green-700' : 'bg-red-500 border-l-4 border-red-600'
          }`}>
            {saveStatus}
          </div>
        )}

        {/* Desktop Header */}
        <div className="hidden lg:flex items-center justify-between gap-3 flex-wrap mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
              OT <span className="text-blue-600">Claims</span>
            </h1>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative min-w-[140px]">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                <FaSearch className="text-sm" />
              </span>
              <input
                type="text"
                name="employeeId"
                placeholder="Search..."
                value={filters.employeeId}
                onChange={handleFilterChange}
                className="w-[140px] pl-9 pr-3 py-1.5 text-xs border border-gray-300 bg-white text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="h-8 px-3 py-1 text-xs border border-gray-300 bg-white text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>

            <div className="relative" ref={departmentFilterRef}>
              <button
                onClick={() => {
                  setShowDepartmentFilter(!showDepartmentFilter);
                  setShowDesignationFilter(false);
                }}
                className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-all bg-white whitespace-nowrap ${
                  filterDepartment
                    ? "border-blue-500 text-blue-700 ring-2 ring-blue-500/10 bg-blue-50"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaBuilding className="text-gray-400 text-[10px]" />
                <span className="truncate max-w-[80px]">{filterDepartment || "Dept"}</span>
                <span className="text-gray-400 text-[10px]">▾</span>
              </button>
              {showDepartmentFilter && (
                <div
                  className="fixed bg-white border border-gray-200 rounded-lg shadow-2xl min-w-[180px] max-h-60 overflow-y-auto"
                  style={{
                    zIndex: 99999,
                    top: departmentFilterRef.current ? departmentFilterRef.current.getBoundingClientRect().bottom + 4 : 'auto',
                    left: departmentFilterRef.current ? departmentFilterRef.current.getBoundingClientRect().left : 'auto',
                  }}
                >
                  <div
                    onClick={() => { setFilterDepartment(""); setShowDepartmentFilter(false); }}
                    className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-100 cursor-pointer hover:bg-blue-50"
                  >
                    All Departments
                  </div>
                  {uniqueDepartments.map((dept) => (
                    <div
                      key={dept}
                      onClick={() => { setFilterDepartment(dept); setShowDepartmentFilter(false); }}
                      className={`px-3 py-2 text-xs cursor-pointer hover:bg-blue-50 ${
                        filterDepartment === dept ? "bg-blue-50 text-blue-700 font-semibold" : "text-gray-700"
                      }`}
                    >
                      {dept}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="relative" ref={designationFilterRef}>
              <button
                onClick={() => {
                  setShowDesignationFilter(!showDesignationFilter);
                  setShowDepartmentFilter(false);
                }}
                className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-all bg-white whitespace-nowrap ${
                  filterDesignation
                    ? "border-blue-500 text-blue-700 ring-2 ring-blue-500/10 bg-blue-50"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaUserTag className="text-gray-400 text-[10px]" />
                <span className="truncate max-w-[80px]">{filterDesignation || "Design"}</span>
                <span className="text-gray-400 text-[10px]">▾</span>
              </button>
              {showDesignationFilter && (
                <div
                  className="fixed bg-white border border-gray-200 rounded-lg shadow-2xl min-w-[180px] max-h-60 overflow-y-auto"
                  style={{
                    zIndex: 99999,
                    top: designationFilterRef.current ? designationFilterRef.current.getBoundingClientRect().bottom + 4 : 'auto',
                    left: designationFilterRef.current ? designationFilterRef.current.getBoundingClientRect().left : 'auto',
                  }}
                >
                  <div
                    onClick={() => { setFilterDesignation(""); setShowDesignationFilter(false); }}
                    className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-100 cursor-pointer hover:bg-blue-50"
                  >
                    All Designations
                  </div>
                  {uniqueDesignations.map((des) => (
                    <div
                      key={des}
                      onClick={() => { setFilterDesignation(des); setShowDesignationFilter(false); }}
                      className={`px-3 py-2 text-xs cursor-pointer hover:bg-blue-50 ${
                        filterDesignation === des ? "bg-blue-50 text-blue-700 font-semibold" : "text-gray-700"
                      }`}
                    >
                      {des}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <input
              type="date"
              name="fromDate"
              value={filters.fromDate}
              onChange={handleFilterChange}
              onClick={(e) => e.target.showPicker && e.target.showPicker()}
              className="w-[110px] h-8 px-2 py-1 text-xs border border-gray-300 bg-white text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />

            <input
              type="date"
              name="toDate"
              value={filters.toDate}
              onChange={handleFilterChange}
              onClick={(e) => e.target.showPicker && e.target.showPicker()}
              className="w-[110px] h-8 px-2 py-1 text-xs border border-gray-300 bg-white text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />

            <button
              onClick={fetchClaims}
              disabled={loading}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all shadow-sm whitespace-nowrap"
            >
              <FiRefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>

            <button
              onClick={exportCSV}
              disabled={claims.length === 0}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all shadow-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiDownload className="w-3 h-3" />
              Export
            </button>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all shadow-sm whitespace-nowrap"
              >
                <FiTrash2 className="w-3 h-3" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between gap-2 flex-wrap mb-3">
          <div>
            <h1 className="text-lg font-bold text-gray-900">
              OT <span className="text-blue-600">Claims</span>
            </h1>
          </div>
        </div>

        {/* Mobile Filters Toggle */}
        <div className="lg:hidden mb-3">
          <div className="flex items-center justify-between p-2.5 bg-white rounded-xl border border-gray-200">
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="flex items-center gap-2 text-sm font-semibold text-gray-700"
            >
              <FiFilter className="text-blue-600 text-base" />
              <span>Filters &amp; Actions</span>
              {showMobileFilters ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
            </button>
            <span className="text-xs text-gray-500">
              <strong>{claims.length}</strong> claims
            </span>
          </div>

          {showMobileFilters && (
            <div className="mt-2 p-4 bg-white rounded-xl border border-gray-200 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    <FaSearch className="text-sm" />
                  </span>
                  <input
                    type="text"
                    name="employeeId"
                    placeholder="Search ID or Name..."
                    value={filters.employeeId}
                    onChange={handleFilterChange}
                    className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-300 bg-white text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 bg-white text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
                  <input
                    type="date"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 bg-white text-gray-900 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
                  <input
                    type="date"
                    name="toDate"
                    value={filters.toDate}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 bg-white text-gray-900 rounded-lg"
                  />
                </div>
              </div>

              <div className="pt-3 border-t border-gray-200 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={fetchClaims}
                    disabled={loading}
                    className="flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all shadow-sm"
                  >
                    <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                  <button
                    onClick={exportCSV}
                    disabled={claims.length === 0}
                    className="flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all shadow-sm disabled:opacity-50"
                  >
                    <FiDownload className="w-4 h-4" />
                    Export
                  </button>
                </div>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
                  >
                    <FiTrash2 className="w-4 h-4" />
                    Clear All Filters
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* KPI Stats Grid */}
        <div className="grid grid-cols-1 gap-3 mb-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-all cursor-pointer">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Total Claims</span>
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <FiList className="text-base" />
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              <CountUp end={summary.totalClaims || 0} duration={1} />
            </div>
            <div className="mt-1 text-xs text-gray-500">total OT claims</div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-all cursor-pointer">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">OT Hours</span>
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-orange-50 text-orange-600">
                <FiClock className="text-base" />
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              <CountUp end={summary.totalOTHours || 0} duration={1} decimals={1} />
            </div>
            <div className="mt-1 text-xs text-gray-500">total overtime hours</div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-all cursor-pointer">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Pending</span>
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                <FiClock className="text-base" />
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              <CountUp end={summary.pendingCount || 0} duration={1} />
            </div>
            <div className="mt-1 text-xs text-gray-500">awaiting approval</div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-all cursor-pointer">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Approved</span>
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-green-50 text-green-600">
                <FiCheckCircle className="text-base" />
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              <CountUp end={summary.approvedCount || 0} duration={1} />
            </div>
            <div className="mt-1 text-xs text-gray-500">approved claims</div>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedClaims.length > 0 && (
          <div className="p-4 mb-6 bg-blue-50 rounded-xl border border-blue-200 flex items-center justify-between shadow-sm">
            <span className="text-sm font-medium text-blue-700 flex items-center gap-2">
              <FiList className="text-blue-600" />
              {selectedClaims.length} claims selected
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setShowBulkActionModal(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all shadow-sm"
              >
                Bulk Actions
              </button>
              <button
                onClick={() => setSelectedClaims([])}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all shadow-sm"
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}

        {/* Main Claims Container */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden mb-6">
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <FiList className="text-blue-600" /> OT Claims List
              </h3>
            </div>
          </div>

          {claims.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-500 font-medium">
              No OT claims found matching current filter values.
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 bg-white">
                  <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <tr>
                      <th className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                      </th>
                      <th style={{ color: 'black' }} className="px-4 py-3 text-left">Employee</th>
                      <th style={{ color: 'black' }} className="px-4 py-3 text-center">Date</th>
                      <th style={{ color: 'black' }} className="px-4 py-3 text-center">OT Hours</th>
                      <th style={{ color: 'black' }} className="px-4 py-3 text-center">Multiplier</th>
                      <th style={{ color: 'black' }} className="px-4 py-3 text-center">OT Amount</th>
                      <th style={{ color: 'black' }} className="px-4 py-3 text-left">Reason</th>
                      <th style={{ color: 'black' }} className="px-4 py-3 text-center">Status</th>
                      <th style={{ color: 'black' }} className="px-4 py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 text-xs">
                    {claims.map((claim) => (
                      <tr key={claim._id} className="hover:bg-gray-50 transition-all">
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedClaims.includes(claim._id)}
                            onChange={() => handleSelectClaim(claim._id)}
                            className="w-4 h-4 rounded border-gray-300"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <div className="font-semibold text-gray-900">{claim.employeeName}</div>
                            <div className="text-xs text-gray-500">{claim.employeeId}</div>
                            <div className="text-xs text-gray-400">{claim.employeeDetails?.department || '-'}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="text-gray-700 font-medium">{formatDate(claim.date)}</div>
                          <div className="text-gray-400 text-xs">{formatTime(claim.date)}</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-orange-50 text-orange-700 border border-orange-200">
                            {claim.otHours}h
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <select
                            value={getMultiplier(claim._id)}
                            onChange={(e) => handleMultiplierChange(claim._id, e.target.value)}
                            className="px-2 py-1 text-xs border border-gray-300 rounded bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          >
                            <option value={1}>1x</option>
                            <option value={1.5}>1.5x</option>
                            <option value={2}>2x</option>
                            <option value={2.5}>2.5x</option>
                            <option value={3}>3x</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-semibold text-green-600">
                            {calculateOTAmount(claim)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="max-w-[200px] text-gray-600 truncate" title={claim.reason}>
                            {claim.reason || '-'}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {getStatusBadge(claim.status)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {/* View Details */}
                            <button
                              onClick={() => { setSelectedClaim(claim); setShowDetailsModal(true); }}
                              className="p-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-all shadow-sm"
                              title="View Details"
                            >
                              <FaEye size={14} />
                            </button>

                            {/* ✅ Update Status — for pending, and revert for approved */}
                            {claim.status === 'pending' && (
                              <button
                                onClick={() => {
                                  setSelectedClaim(claim);
                                  setStatusUpdate({ status: 'approved', rejectedReason: '', notes: '' });
                                  setShowStatusModal(true);
                                }}
                                className="p-1.5 text-green-600 bg-green-50 hover:bg-green-100 rounded-md transition-all shadow-sm"
                                title="Approve / Reject"
                              >
                                <FaCheck size={14} />
                              </button>
                            )}

                            {/* ✅ REVERT button for approved claims */}
                            {claim.status === 'approved' && (
                              <button
                                onClick={() => {
                                  setSelectedClaim(claim);
                                  setShowRevertModal(true);
                                }}
                                className="p-1.5 text-amber-600 bg-amber-50 hover:bg-amber-100 rounded-md transition-all shadow-sm"
                                title="Revert Approval (undo)"
                              >
                                <FaUndo size={14} />
                              </button>
                            )}

                            {/* ✅ Change status for rejected (allow re-approve) */}
                            {claim.status === 'rejected' && (
                              <button
                                onClick={() => {
                                  setSelectedClaim(claim);
                                  setStatusUpdate({ status: 'approved', rejectedReason: '', notes: '' });
                                  setShowStatusModal(true);
                                }}
                                className="p-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-all shadow-sm"
                                title="Re-Approve"
                              >
                                <FaCheck size={14} />
                              </button>
                            )}

                            {/* ✅ Delete — available for ALL statuses */}
                            <button
                              onClick={() => { setSelectedClaim(claim); setShowDeleteModal(true); }}
                              className="p-1.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-all shadow-sm"
                              title="Delete"
                            >
                              <FaTrash size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile View */}
              <div className="block lg:hidden divide-y divide-gray-100">
                {claims.map((claim) => (
                  <div key={claim._id} className="p-4 bg-white">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedClaims.includes(claim._id)}
                          onChange={() => handleSelectClaim(claim._id)}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                        <div>
                          <h4 className="font-semibold text-gray-900">{claim.employeeName}</h4>
                          <span className="text-xs text-gray-500">{claim.employeeId} • {claim.employeeDetails?.department || '-'}</span>
                        </div>
                      </div>
                      {getStatusBadge(claim.status)}
                    </div>

                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs mb-3 text-gray-600">
                      <div><span className="text-gray-400">Date:</span> {formatDate(claim.date)}</div>
                      <div><span className="text-gray-400">OT Hours:</span> <span className="font-semibold text-orange-600">{claim.otHours}h</span></div>
                      <div><span className="text-gray-400">Multiplier:</span>
                        <select
                          value={getMultiplier(claim._id)}
                          onChange={(e) => handleMultiplierChange(claim._id, e.target.value)}
                          className="ml-1 px-1 py-0.5 text-xs border border-gray-300 rounded bg-white text-gray-900"
                        >
                          <option value={1}>1x</option>
                          <option value={1.5}>1.5x</option>
                          <option value={2}>2x</option>
                          <option value={2.5}>2.5x</option>
                          <option value={3}>3x</option>
                        </select>
                      </div>
                      <div><span className="text-gray-400">OT Amount:</span> <span className="font-semibold text-green-600">{calculateOTAmount(claim)}</span></div>
                      <div className="col-span-2"><span className="text-gray-400">Reason:</span> {claim.reason || '-'}</div>
                    </div>

                    <div className="flex items-center justify-end gap-1.5 pt-3 border-t border-gray-100 flex-wrap">
                      <button
                        onClick={() => { setSelectedClaim(claim); setShowDetailsModal(true); }}
                        className="px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-all"
                      >
                        View
                      </button>

                      {claim.status === 'pending' && (
                        <button
                          onClick={() => {
                            setSelectedClaim(claim);
                            setStatusUpdate({ status: 'approved', rejectedReason: '', notes: '' });
                            setShowStatusModal(true);
                          }}
                          className="px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 rounded-md transition-all"
                        >
                          Approve/Reject
                        </button>
                      )}

                      {claim.status === 'approved' && (
                        <button
                          onClick={() => { setSelectedClaim(claim); setShowRevertModal(true); }}
                          className="px-3 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-md transition-all"
                        >
                          Revert
                        </button>
                      )}

                      {claim.status === 'rejected' && (
                        <button
                          onClick={() => {
                            setSelectedClaim(claim);
                            setStatusUpdate({ status: 'approved', rejectedReason: '', notes: '' });
                            setShowStatusModal(true);
                          }}
                          className="px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-all"
                        >
                          Re-Approve
                        </button>
                      )}

                      <button
                        onClick={() => { setSelectedClaim(claim); setShowDeleteModal(true); }}
                        className="px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 rounded-md transition-all"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Pagination */}
        {claims.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-white border border-gray-200 rounded-2xl shadow-sm">
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
              <span>Show</span>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="p-1 border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span>entries</span>
              <span className="hidden sm:inline text-gray-300">|</span>
              <span className="text-[10px] sm:text-xs">
                Showing <strong>{Math.min((currentPage - 1) * itemsPerPage + 1, summary.totalClaims || 0)}</strong> to <strong>{Math.min(currentPage * itemsPerPage, summary.totalClaims || 0)}</strong> of <strong>{summary.totalClaims || 0}</strong> claims
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={`px-2.5 py-1 text-xs font-semibold border rounded-lg transition-all ${
                  currentPage === 1
                    ? 'text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed'
                    : 'text-gray-700 bg-white hover:bg-gray-50 border-gray-300 shadow-sm'
                }`}
              >
                Prev
              </button>

              {getPageNumbers().map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 text-xs font-semibold border rounded-lg transition-all min-w-[32px] ${
                    currentPage === page
                      ? 'text-white bg-blue-600 border-blue-600 shadow-sm'
                      : 'text-gray-700 bg-white hover:bg-gray-50 border-gray-300'
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => setCurrentPage(p => Math.min(summary.totalPages || 1, p + 1))}
                disabled={currentPage === (summary.totalPages || 1)}
                className={`px-2.5 py-1 text-xs font-semibold border rounded-lg transition-all ${
                  currentPage === (summary.totalPages || 1)
                    ? 'text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed'
                    : 'text-gray-700 bg-white hover:bg-gray-50 border-gray-300 shadow-sm'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* ==================== MODALS ==================== */}

        {/* Details Modal */}
        {showDetailsModal && selectedClaim && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-white/40 backdrop-blur-xs">
            <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-gray-150 max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 z-10 p-4 bg-white border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <FiList className="text-blue-600" /> Claim Details
                  </h2>
                  <button
                    onClick={() => { setShowDetailsModal(false); setSelectedClaim(null); }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-100">
                  <h3 className="mb-3 font-semibold text-gray-700 flex items-center gap-2">
                    <FiList className="text-blue-600" /> Employee Information
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex justify-between"><span className="text-gray-500">Name:</span> <span className="font-medium text-gray-900">{selectedClaim.employeeName}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">ID:</span> <span className="font-medium text-gray-900">{selectedClaim.employeeId}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Department:</span> <span className="font-medium text-gray-900">{selectedClaim.employeeDetails?.department || 'N/A'}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Email:</span> <span className="font-medium text-gray-900">{selectedClaim.employeeDetails?.email || 'N/A'}</span></div>
                    <div className="flex justify-between col-span-2"><span className="text-gray-500">Salary/Month:</span> <span className="font-medium text-gray-900">₹{selectedClaim.employeeDetails?.salaryPerMonth?.toLocaleString() || '0'}</span></div>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <h3 className="mb-3 font-semibold text-gray-700 flex items-center gap-2">
                    <FiClock className="text-orange-600" /> Claim Information
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex justify-between"><span className="text-gray-500">Date:</span> <span className="font-medium text-gray-900">{formatDate(selectedClaim.date)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">OT Hours:</span> <span className="font-bold text-orange-600">{selectedClaim.otHours}h</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Multiplier:</span> <span className="font-medium text-gray-900">{getMultiplier(selectedClaim._id)}x</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">OT Amount:</span> <span className="font-bold text-green-600">{calculateOTAmount(selectedClaim)}</span></div>
                    <div className="flex justify-between col-span-2"><span className="text-gray-500">Reason:</span> <span className="font-medium text-gray-900">{selectedClaim.reason}</span></div>
                    <div className="flex justify-between col-span-2"><span className="text-gray-500">Status:</span> {getStatusBadge(selectedClaim.status)}</div>
                    {selectedClaim.status === 'approved' && (
                      <>
                        <div className="flex justify-between"><span className="text-gray-500">Approved By:</span> <span className="font-medium text-gray-900">{selectedClaim.approvedBy || 'Admin'}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Approved At:</span> <span className="font-medium text-gray-900">{formatDate(selectedClaim.approvedAt)}</span></div>
                      </>
                    )}
                    {selectedClaim.status === 'rejected' && (
                      <div className="flex justify-between col-span-2"><span className="text-gray-500">Rejected Reason:</span> <span className="font-medium text-gray-900">{selectedClaim.rejectedReason || 'N/A'}</span></div>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  {selectedClaim.status === 'pending' && (
                    <button
                      onClick={() => { setShowDetailsModal(false); setShowStatusModal(true); }}
                      className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all shadow-sm"
                    >
                      Approve/Reject
                    </button>
                  )}
                  {selectedClaim.status === 'approved' && (
                    <button
                      onClick={() => { setShowDetailsModal(false); setShowRevertModal(true); }}
                      className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-all shadow-sm"
                    >
                      Revert Approval
                    </button>
                  )}
                  {selectedClaim.status === 'rejected' && (
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        setStatusUpdate({ status: 'approved', rejectedReason: '', notes: '' });
                        setShowStatusModal(true);
                      }}
                      className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all shadow-sm"
                    >
                      Re-Approve
                    </button>
                  )}
                  <button
                    onClick={() => { setShowDetailsModal(false); setShowDeleteModal(true); }}
                    className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-all shadow-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ✅ Revert Modal */}
        {showRevertModal && selectedClaim && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-white/40 backdrop-blur-xs">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-150">
              <div className="p-4 border-b border-gray-100">
                <h2 className="text-xl font-bold text-amber-600 flex items-center gap-2">
                  <FaUndo className="text-amber-600" /> Revert Approved OT
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedClaim.employeeName} - {formatDate(selectedClaim.date)}
                </p>
              </div>

              <div className="p-6">
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <p className="text-sm text-amber-800 font-semibold mb-2">
                    ⚠️ Reverting will:
                  </p>
                  <ul className="text-sm text-amber-700 ml-4 list-disc space-y-1">
                    <li>Remove <strong>₹{calculateOTAmount(selectedClaim)}</strong> from payroll</li>
                    <li>Change status from <strong>Approved</strong> back to <strong>Pending</strong></li>
                    <li>Payroll will no longer include this OT</li>
                  </ul>
                </div>

                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex justify-between"><span className="text-gray-500">Employee:</span> <span className="font-medium">{selectedClaim.employeeName}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">OT Hours:</span> <span className="font-medium">{selectedClaim.otHours}h</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Multiplier:</span> <span className="font-medium">{getMultiplier(selectedClaim._id)}x</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Amount:</span> <span className="font-medium text-green-600">{calculateOTAmount(selectedClaim)}</span></div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => { setShowRevertModal(false); setSelectedClaim(null); }}
                    className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRevertApproved}
                    className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-all shadow-sm"
                  >
                    Yes, Revert
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Status Update Modal */}
        {showStatusModal && selectedClaim && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-white/40 backdrop-blur-xs">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-150">
              <div className="p-4 border-b border-gray-100">
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <FiCheckCircle className="text-blue-600" /> Update Claim Status
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedClaim.employeeName} - {formatDate(selectedClaim.date)}
                </p>
                <p className="text-sm text-green-600 font-medium mt-2">
                  OT Amount: {calculateOTAmount(selectedClaim)} at {getMultiplier(selectedClaim._id)}x
                </p>
                {selectedClaim.status !== 'pending' && (
                  <p className="text-xs text-blue-600 mt-1">
                    Current Status: {selectedClaim.status}
                  </p>
                )}
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={statusUpdate.status}
                    onChange={(e) => setStatusUpdate(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="approved">✅ Approved</option>
                    <option value="rejected">❌ Rejected</option>
                    <option value="pending">⏳ Pending</option>
                  </select>
                </div>

                {statusUpdate.status === 'rejected' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Rejection Reason</label>
                    <textarea
                      value={statusUpdate.rejectedReason}
                      onChange={(e) => setStatusUpdate(prev => ({ ...prev, rejectedReason: e.target.value }))}
                      placeholder="Why is this claim being rejected?"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      rows="3"
                      required
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
                  <textarea
                    value={statusUpdate.notes}
                    onChange={(e) => setStatusUpdate(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Add any notes..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    rows="2"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => { setShowStatusModal(false); setSelectedClaim(null); }}
                    className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleStatusUpdate}
                    className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all shadow-sm"
                  >
                    Update Status
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {showDeleteModal && selectedClaim && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-white/40 backdrop-blur-xs">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-150">
              <div className="p-4 border-b border-gray-100">
                <h2 className="text-xl font-bold text-red-600 flex items-center gap-2">
                  <FiTrash2 className="text-red-600" /> Delete Claim
                </h2>
              </div>

              <div className="p-6">
                <p className="text-gray-700 mb-4">
                  Are you sure you want to delete this OT claim?
                </p>
                <div className="p-4 bg-red-50 rounded-xl border border-red-100">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between"><span className="text-gray-500">Employee:</span> <span className="font-medium text-gray-900">{selectedClaim.employeeName}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Date:</span> <span className="font-medium text-gray-900">{formatDate(selectedClaim.date)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">OT Hours:</span> <span className="font-medium text-gray-900">{selectedClaim.otHours}h</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Multiplier:</span> <span className="font-medium text-gray-900">{getMultiplier(selectedClaim._id)}x</span></div>
                    <div className="flex justify-between col-span-2"><span className="text-gray-500">OT Amount:</span> <span className="font-medium text-gray-900">{calculateOTAmount(selectedClaim)}</span></div>
                    <div className="flex justify-between col-span-2"><span className="text-gray-500">Status:</span> {getStatusBadge(selectedClaim.status)}</div>
                  </div>
                </div>

                {selectedClaim.status === 'approved' && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-700 font-semibold">
                      ⚠️ This claim is currently APPROVED. Deleting it will remove <strong>₹{calculateOTAmount(selectedClaim)}</strong> from payroll.
                    </p>
                  </div>
                )}

                <p className="mt-4 text-sm text-red-600 font-medium flex items-center gap-2">
                  <span>⚠️</span> This action cannot be undone.
                </p>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => { setShowDeleteModal(false); setSelectedClaim(null); }}
                    className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-all shadow-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Action Modal */}
        {showBulkActionModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-white/40 backdrop-blur-xs">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-150">
              <div className="p-4 border-b border-gray-100">
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <FiList className="text-blue-600" /> Bulk Actions
                </h2>
                <p className="text-sm text-gray-500 mt-1">{selectedClaims.length} claims selected</p>
              </div>

              <div className="p-6 space-y-3">
                <button
                  onClick={() => handleBulkAction('status', 'approved')}
                  className="w-full p-3 text-left text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-all flex items-center gap-3 border border-green-200"
                >
                  <FiCheckCircle className="text-green-600" /> Approve All
                </button>
                <button
                  onClick={() => handleBulkAction('status', 'rejected')}
                  className="w-full p-3 text-left text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-all flex items-center gap-3 border border-red-200"
                >
                  <FiXCircle className="text-red-600" /> Reject All
                </button>
                <button
                  onClick={() => {
                    if (window.confirm(`Delete ${selectedClaims.length} claims?`)) {
                      handleBulkAction('delete', null);
                    }
                  }}
                  className="w-full p-3 text-left text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-all flex items-center gap-3 border border-red-200"
                >
                  <FiTrash2 className="text-red-600" /> Delete All
                </button>

                <button
                  onClick={() => setShowBulkActionModal(false)}
                  className="w-full p-2.5 mt-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all shadow-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <style>{`
          @keyframes fade-in {
            from { opacity: 0; transform: translateY(-10px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in { animation: fade-in 0.3s ease-out; }
        `}</style>
      </div>
    </div>
  );
}